/**
 * @module App
 * @description Raíz de la aplicación ACSGC: árbol de providers y enrutamiento de vistas.
 *
 * Monta todos los contextos React en orden de dependencia (de más externo a más interno):
 * ThemeProvider → AuthProvider → FrameworkProvider → DataProvider → AuditProvider →
 * AssetProvider → SecurityEventsProvider → AIProvider → OrganizationProvider →
 * ISO27001Provider → LMCSProvider → CISProvider → ACSProvider → AmbientModeProvider
 *
 * Cada provider está envuelto en un `ErrorBoundary` independiente para que un fallo
 * en un módulo no derrumbe toda la aplicación — solo el módulo afectado queda en error.
 *
 * ## Componentes internos
 * - `AmbientOverlayWrapper`: gestiona el fade-in/out del overlay de modo ambient.
 *   Vive dentro de `AmbientModeProvider` para poder leer el contexto.
 * - `AppContent`: decide qué renderizar según el estado de autenticación
 *   (loading spinner → LoginForm → ResetPasswordForm → MainLayout).
 *
 * ## Notas de diseño
 * - `CertificationProvider` está temporalmente deshabilitado: la tabla `certifications`
 *   no existe aún en la DB. Reactivar cuando se ejecute la migración correspondiente.
 * - `FADE_OUT_MS = 450` debe sincronizarse con la duración CSS del overlay.
 */
import React, { useEffect, useState } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { AuditProvider } from './contexts/AuditContext';
import { AssetProvider } from './contexts/AssetContext'; // Módulo de Activos de Información
import { SecurityEventsProvider } from './contexts/SecurityEventsContext'; // Eventos de Ciberseguridad
import { AIProvider } from './contexts/AIContext'; // ✅ NUEVO: AI Provider
import { FrameworkProvider } from './contexts/FrameworkContext'; // Marcos normativos (ISO, Ley Marco, etc.)
import { OrganizationProvider } from './contexts/OrganizationContext'; // Necesario para MainLayout (determina vista inicial)
import { ISO27001Provider } from './contexts/ISO27001Context'; // ISO 27001:2022 SGSI
import { LMCSProvider } from './contexts/LMCSContext'; // Ley Marco de Ciberseguridad (Ley 21.663)
import { CISProvider } from './contexts/CISContext'; // CIS Controls v8
import { ACSProvider } from './contexts/ACSContext'; // Asistente Conversacional Supervisado
import { AmbientModeProvider, useAmbientMode } from './contexts/AmbientModeContext'; // Modo Ambient
import { AmbientModeOverlay } from './components/AmbientMode';
// import { CertificationProvider } from './contexts/CertificationContext'; // ⚠️ TEMPORALMENTE DESHABILITADO: tabla certifications no existe
import { ThemeProvider } from './contexts/ThemeContext';
import { LoginForm } from './components/Auth/LoginForm';
import { ResetPasswordForm } from './components/Auth/ResetPasswordForm';
import { ChangePasswordModal } from './components/Auth/ChangePasswordModal';
import { MainLayout } from './components/Layout/MainLayout';
import { useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'; // ✅ NUEVO: Error Boundary
// import { DebugPanel } from './components/Debug/DebugPanel'; // TEMPORALMENTE DESACTIVADO

const FADE_OUT_MS = 450;

/**
 * Renderiza el overlay con fade-in/out correcto.
 * Vive dentro de AmbientModeProvider para poder leer el contexto.
 */
function AmbientOverlayWrapper() {
  const { isAmbient } = useAmbientMode();
  const [showOverlay, setShowOverlay] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (isAmbient) {
      setShowOverlay(true);
      setFadingOut(false);
    } else if (showOverlay) {
      setFadingOut(true);
      const t = setTimeout(() => {
        setShowOverlay(false);
        setFadingOut(false);
      }, FADE_OUT_MS);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAmbient]);

  if (!showOverlay) return null;
  return <AmbientModeOverlay fadingOut={fadingOut} />;
}

function AppContent() {
  const { user, isLoading, isRecoveryMode } = useAuth();

  // Mostrar panel de debug en desarrollo (TEMPORALMENTE DESACTIVADO)
  const showDebugPanel = false; // import.meta.env.DEV;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando...</p>
          <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">
            Si esto tarda más de 20 segundos, revisa la consola (F12)
          </p>
        </div>
        {showDebugPanel && <DebugPanel />}
      </div>
    );
  }

  if (isRecoveryMode) {
    return <ResetPasswordForm />;
  }

  if (!user) {
    return (
      <>
        <LoginForm />
        {showDebugPanel && <DebugPanel />}
      </>
    );
  }

  return (
    <>
      <MainLayout />
      {showDebugPanel && <DebugPanel />}
      {/* Modal de cambio de contraseña obligatorio */}
      <ChangePasswordModal isOpen={user.force_password_change === true} />
    </>
  );
}

function App() {
  return (
    // ✅ CORRECCIÓN CRÍTICA: Error Boundary en nivel raíz
    <ErrorBoundary boundaryName="App Root">
      <ThemeProvider>
        <ErrorBoundary boundaryName="Auth Layer">
          <AuthProvider>
            <ErrorBoundary boundaryName="Framework Layer">
              <FrameworkProvider>
                <ErrorBoundary boundaryName="Data Layer">
                  <DataProvider>
                    <ErrorBoundary boundaryName="Audit Layer">
                      <AuditProvider>
                        <ErrorBoundary boundaryName="Asset Layer">
                          <AssetProvider>
                            <ErrorBoundary boundaryName="SecurityEvents Layer">
                              <SecurityEventsProvider>
                                <ErrorBoundary boundaryName="AI Layer">
                                  <AIProvider>
                                {/* OrganizationProvider restaurado (necesario para MainLayout) pero con carga silenciosa */}
                                <ErrorBoundary boundaryName="Organization Layer">
                                  <OrganizationProvider>
                                    <ErrorBoundary boundaryName="ISO 27001 Layer">
                                      <ISO27001Provider>
                                        <ErrorBoundary boundaryName="LMCS Layer">
                                          <LMCSProvider>
                                            <ErrorBoundary boundaryName="CIS Layer">
                                              <CISProvider>
                                            <ErrorBoundary boundaryName="ACS Layer">
                                              <ACSProvider>
                                                <ErrorBoundary boundaryName="Ambient Layer">
                                                  <AmbientModeProvider>
                                                    {/* ⚠️ TEMPORALMENTE DESHABILITADO: tabla certifications no existe */}
                                                    {/* <ErrorBoundary boundaryName="Certification Layer">
                                                      <CertificationProvider> */}
                                                        <AppContent />
                                                        <AmbientOverlayWrapper />
                                                      {/* </CertificationProvider>
                                                    </ErrorBoundary> */}
                                                  </AmbientModeProvider>
                                                </ErrorBoundary>
                                              </ACSProvider>
                                            </ErrorBoundary>
                                              </CISProvider>
                                            </ErrorBoundary>
                                          </LMCSProvider>
                                        </ErrorBoundary>
                                      </ISO27001Provider>
                                    </ErrorBoundary>
                                  </OrganizationProvider>
                                </ErrorBoundary>
                                  </AIProvider>
                                </ErrorBoundary>
                              </SecurityEventsProvider>
                            </ErrorBoundary>
                          </AssetProvider>
                        </ErrorBoundary>
                      </AuditProvider>
                    </ErrorBoundary>
                  </DataProvider>
                </ErrorBoundary>
              </FrameworkProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;